import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IAccount, IAppManagement, IAppManagementPayload } from '~/types/application-management'
import type { TPlatformType } from '~/types/device-type'

import {
  getAccount,
  getAppDetails,
  getAppsManagerList,
  updateAccount,
  setApp,
  setService,
} from '~/api/application-management'

////////// KEYS //////////

export const keys = {
  account: (accountKey: string) => [...keys.all, 'account', accountKey] as const,
  all: ['apps-management'] as const,
  details: (appKey?: string) => [...keys.all, 'details', appKey] as const,
  list: () => [...keys.all, 'list'] as const,
}

////////// QUERIES //////////

export const useAppsManagementListQuery = (): UseQueryResult<IAppManagement[]> => {
  return useQuery({
    queryFn: getAppsManagerList,
    queryKey: keys.list(),
  })
}

export const useAppsManagementDetailsQuery = (appKey?: string): UseQueryResult<IAppManagement> => {
  return useQuery({
    queryFn: appKey ? () => getAppDetails(appKey) : skipToken,
    queryKey: keys.details(appKey),
  })
}

/** ACCOUNT */

export const useAccountQuery = (accountKey: string): UseQueryResult<IAccount> => {
  return useQuery({
    queryFn: () => getAccount(accountKey),
    queryKey: keys.account(accountKey),
  })
}

////////// MUTATIONS //////////

export const useSetAppMutation = (): UseMutationResult<IAppManagement, unknown, IAppManagementPayload> => {
  return useMutation({ mutationFn: setApp })
}

/** ACCOUNT */

export const useUpdateAccountMutation = (): UseMutationResult<IAccount, unknown, IAccount> => {
  return useMutation({ mutationFn: updateAccount })
}

/** SERVICES */

export const useSetServiceMutation = (): UseMutationResult<
  void,
  unknown,
  { additionalConfig: string; appKey: string; enabled: boolean; platform: TPlatformType; service: string }
> => {
  return useMutation({ mutationFn: setService })
}
